/**
 * Ensures an agent draft opens the RFQ Builder at most once, deduping
 * across observer remounts and reloads within a tab via a bounded
 * localStorage set.
 *
 * Scope note: cross-tab is handled upstream by the per-app-window handoff
 * token — each tab mints its own, so only the initiating tab ever tries
 * to open a given draft. This store's read → includes → write is NOT
 * atomic, so it does not itself guard two tabs racing on the same id; it
 * does not need to, because that case is already excluded by the per-tab
 * token.
 */

const STORAGE_KEY = 'dime.opened-draft-rfq-ids';
const MAX_IDS = 200;

function readClaimed(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const parsed: unknown = raw == null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function writeClaimed(ids: readonly string[]): void {
  try {
    // Keep the set bounded — most recent ids win.
    const bounded = ids.slice(-MAX_IDS);
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(bounded));
  } catch {
    // No storage (private mode / SSR) — degrade to no dedup rather than throw.
  }
}

/**
 * Returns `true` the first time it is called for `id`, and `false` for
 * every subsequent call (across remounts, reloads, and same-origin tabs).
 * So the RFQ Builder opens exactly once per draft.
 */
export function claimDraftOnce(id: string): boolean {
  const claimed = readClaimed();
  if (claimed.includes(id)) return false;
  writeClaimed([...claimed, id]);
  return true;
}

/**
 * Releases a claim so a later attempt can re-open the draft. Call this
 * when the open failed after claiming (e.g. the builder payload could not
 * be built or the draft fetch errored), otherwise the id is burned and no
 * remount/reload would ever retry it.
 */
export function releaseDraftClaim(id: string): void {
  const claimed = readClaimed();
  if (!claimed.includes(id)) return;
  writeClaimed(claimed.filter((claimedId) => claimedId !== id));
}

/** Test seam — clears the persisted claim set. */
export function resetDraftClaims(): void {
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
